import React, { useState, useEffect } from "react";
import { Button } from "../ui/components/Button";
import { Badge } from "../ui/components/Badge";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { FeatherX, FeatherFileText, FeatherBook } from "@subframe/core";
import { GSTR2BRecord, ReconciliationStatus, ExclusionReason, PurchaseRegisterInvoice } from "../types/gstr2bTypes";
import { gstr2bAPI } from "../services/gstr2bApi";
import InvoicePickerModal from "./InvoicePickerModal";
import MatchConfirmationModal from "./MatchConfirmationModal";

interface InvoiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: GSTR2BRecord | null;
  onVerificationSuccess?: () => void;
}

const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({
  isOpen,
  onClose,
  record,
  onVerificationSuccess,
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [showInvoicePicker, setShowInvoicePicker] = useState(false);
  const [showMatchConfirmation, setShowMatchConfirmation] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<PurchaseRegisterInvoice | null>(null);
  const [showExcludeDropdown, setShowExcludeDropdown] = useState(false);
  const [excludeReason, setExcludeReason] = useState<ExclusionReason | ''>('');
  const [isExcluding, setIsExcluding] = useState(false);
  const [showVerifyDropdown, setShowVerifyDropdown] = useState(false);
  const [verifyReason, setVerifyReason] = useState<'manual_match' | 'others' | ''>('');
  const [verifyComment, setVerifyComment] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [isUnmatching, setIsUnmatching] = useState(false);
  const [logs, setLogs] = useState<Array<{
    id: number; action: string; from_status: string | null; to_status: string | null;
    matched_bill_id: number | null; notes: string | null; performed_by: number | null; created_at: string;
  }>>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && record && record.status !== 'missing_in_2b') {
      setLogsLoading(true);
      gstr2bAPI.getInvoiceLogs(record.id).then((res) => {
        if (res.success && res.data?.logs) {
          setLogs(res.data.logs);
        } else {
          setLogs([]);
        }
      }).finally(() => setLogsLoading(false));
    } else {
      setLogs([]);
    }
  }, [isOpen, record?.id]);

  if (!isOpen || !record) return null;

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get status badge
  const getStatusBadge = (status: ReconciliationStatus) => {
    switch (status) {
      case 'matched':
        return { variant: 'success' as const, label: 'Matched' };
      case 'suggestive_invoice_mismatch':
        return { variant: 'warning' as const, label: 'Invoice Mismatch' };
      case 'suggestive_date_mismatch':
        return { variant: 'warning' as const, label: 'Date Mismatch' };
      case 'suggestive_amount_mismatch':
        return { variant: 'warning' as const, label: 'Amount Mismatch' };
      case 'missing_in_books':
        return { variant: 'error' as const, label: 'Missing in Books' };
      case 'missing_in_2b':
        return { variant: 'error' as const, label: 'Missing in 2B' };
      case 'excluded':
        return { variant: 'neutral' as const, label: 'Excluded' };
      default:
        return { variant: 'neutral' as const, label: 'Pending' };
    }
  };

  const statusBadge = getStatusBadge(record.status);
  
  // Helper function to check if values are mismatched (with ±1 tolerance for amounts)
  const isMismatch = (books: number | string | null, gstr2b: number | string | null, tolerance = 1): boolean => {
    if (books === null && gstr2b === null) return false;
    if (books === null || gstr2b === null) return true;
    
    if (typeof books === 'number' && typeof gstr2b === 'number') {
      return Math.abs(books - gstr2b) > tolerance;
    }
    
    return books !== gstr2b;
  };

  // Check for mismatches in each field
  const invoiceNumberMismatch = isMismatch(
    record.booksInvoiceNumber || record.invoiceNumber, 
    record.invoiceNumber, 
    0
  );
  const invoiceDateMismatch = isMismatch(
    record.booksInvoiceDate || record.invoiceDate, 
    record.invoiceDate, 
    0
  );
  const taxableValueMismatch = isMismatch(record.booksTaxableValue, record.gstr2bTaxableValue);
  const igstMismatch = isMismatch(record.booksIGST, record.gstr2bIGST);
  const cgstMismatch = isMismatch(record.booksCGST, record.gstr2bCGST);
  const sgstMismatch = isMismatch(record.booksSGST, record.gstr2bSGST);
  const totalAmountMismatch = isMismatch(record.booksAmount, record.gstr2bAmount);

  // Check if this record can be verified.
  // For missing_in_books: only show when user has manually selected an invoice to match.
  const canBeVerified = ['suggestive_invoice_mismatch', 'suggestive_date_mismatch', 'suggestive_amount_mismatch'].includes(record.status)
    || (record.status === 'missing_in_books' && selectedInvoice !== null);

  // Check if this is a missing in books record
  const isMissingInBooks = record.status === 'missing_in_books';
  
  // Check if this is a missing in 2B record
  const isMissingIn2B = record.status === 'missing_in_2b';

  // Whether there is an actual books entry to show on the left column.
  // Books data comes either from a linked purchase-register bill (matchedBillId)
  // or from a purchase-register-side record (id prefixed with 'PR_', e.g. missing_in_2b
  // and excluded PR entries). When neither holds — e.g. a pending/unmatched 2B invoice —
  // the books amounts are all 0 and the identifier fields fall back to GSTR-2B values,
  // which is misleading. In that case show an explicit "no linked books entry" state.
  const hasLinkedBooks = !!record.matchedBillId || record.id.startsWith('PR_');

  // Handle mark as verified
  const handleMarkAsVerified = async () => {
    if (!verifyReason) {
      setVerificationError('Please select a verification reason');
      return;
    }
    if (verifyReason === 'others' && !verifyComment.trim()) {
      setVerificationError('Please enter a comment for the verification');
      return;
    }

    // Compose notes from the selected reason (+ comment for "Others")
    const notes = verifyReason === 'manual_match'
      ? 'Manual Match'
      : verifyComment.trim();

    try {
      setIsVerifying(true);
      setVerificationError(null);

      const response = await gstr2bAPI.markAsVerified(record.id, notes);

      if (response.success) {
        // Success - reset, close modal and refresh data
        setShowVerifyDropdown(false);
        setVerifyReason('');
        setVerifyComment('');
        onClose();
        if (onVerificationSuccess) {
          onVerificationSuccess();
        }
      } else {
        setVerificationError(response.message || 'Failed to verify invoice');
      }
    } catch (error: any) {
      setVerificationError(error.message || 'An error occurred');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle match another button click
  const handleMatchAnother = () => {
    setShowInvoicePicker(true);
  };

  // Handle invoice selection from picker
  const handleInvoiceSelected = (invoice: PurchaseRegisterInvoice) => {
    setSelectedInvoice(invoice);
    setShowInvoicePicker(false);
    setShowMatchConfirmation(true);
  };

  // Handle match confirmation
  const handleConfirmMatch = async (notes: string) => {
    if (!selectedInvoice) return;

    const response = await gstr2bAPI.createManualMatch(record.id, selectedInvoice.id, notes);

    if (response.success) {
      setShowMatchConfirmation(false);
      setSelectedInvoice(null);
      onClose();
      if (onVerificationSuccess) {
        onVerificationSuccess();
      }
    } else {
      setVerificationError(response.message || 'Failed to create manual match');
    }
  };

  const handleExclude = async () => {
    if (!excludeReason) {
      setVerificationError('Please select a reason for exclusion');
      return;
    }
    try {
      setIsExcluding(true);
      setVerificationError(null);
      let response;
      if (isMissingIn2B) {
        const prId = parseInt(record.id.replace('PR_', ''), 10);
        response = await gstr2bAPI.excludePurchaseRegisterEntry(prId, excludeReason);
      } else {
        response = await gstr2bAPI.excludeInvoice(record.id, excludeReason);
      }
      if (response.success) {
        setShowExcludeDropdown(false);
        setExcludeReason('');
        onClose();
        if (onVerificationSuccess) onVerificationSuccess();
      } else {
        setVerificationError(response.message || 'Failed to exclude invoice');
      }
    } catch (error: any) {
      setVerificationError(error.message || 'An error occurred');
    } finally {
      setIsExcluding(false);
    }
  };

  const handleRestore = async () => {
    try {
      setIsRestoring(true);
      setVerificationError(null);
      let response;
      if (record.id.startsWith('PR_')) {
        const prId = parseInt(record.id.replace('PR_', ''), 10);
        response = await gstr2bAPI.restorePurchaseRegisterEntry(prId);
      } else {
        response = await gstr2bAPI.restoreInvoice(record.id);
      }
      if (response.success) {
        onClose();
        if (onVerificationSuccess) onVerificationSuccess();
      } else {
        setVerificationError(response.message || 'Failed to restore invoice');
      }
    } catch (error: any) {
      setVerificationError(error.message || 'An error occurred');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleUnmatch = async () => {
    const confirmed = window.confirm('This will unmatch this invoice and move it back to pending. Continue?');
    if (!confirmed) return;
    try {
      setIsUnmatching(true);
      setVerificationError(null);
      const response = await gstr2bAPI.unmatchInvoice(record.id);
      if (response.success) {
        onClose();
        if (onVerificationSuccess) onVerificationSuccess();
      } else {
        setVerificationError(response.message || 'Failed to unmatch invoice');
      }
    } catch (error: any) {
      setVerificationError(error.message || 'An error occurred');
    } finally {
      setIsUnmatching(false);
    }
  };

  // Check if credit lapse reason should be enabled
  // ITC must be claimed before Sept return of the next FY
  // E.g., for FY 2024-25 (Apr 2024 - Mar 2025), deadline is Sept 2025
  const isCreditLapseEnabled = (() => {
    const invoiceDate = new Date(record.invoiceDate);
    if (isNaN(invoiceDate.getTime())) return false;
    const fy = invoiceDate.getMonth() < 3
      ? invoiceDate.getFullYear() - 1
      : invoiceDate.getFullYear();
    const deadline = new Date(fy + 1, 8, 30); // Sept 30 of next FY
    return new Date() > deadline;
  })();

  const isExcluded = record.status === 'excluded';

  const exclusionReasonLabels: Record<string, string> = {
    blocked: 'Blocked ITC',
    personal_expense: 'Not to claim / Personal expense',
    credit_lapsed: 'Credit lapsed (due date crossed)',
  };

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-border px-6 py-4">
            <div className="flex items-center gap-3">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Review Invoice Details
              </span>
              <Badge variant={statusBadge.variant}>
                {statusBadge.label}
              </Badge>
            </div>
            <button
              onClick={onClose}
              className="text-subtext-color hover:text-default-font transition-colors"
            >
              <FeatherX className="h-5 w-5" />
            </button>
          </div>

          {/* Main Content - Single or Two Columns */}
          <div className={`px-6 py-6 ${isMissingIn2B ? '' : 'grid grid-cols-2 gap-6'}`}>
            {/* Left Column - Books Entry */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 mb-2">
                <IconWithBackground
                  variant="neutral"
                  size="small"
                  icon={<FeatherBook />}
                />
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Books Entry
                </span>
              </div>

              {isMissingInBooks ? (
                // Show button for missing in books
                <div className="flex flex-col items-center justify-center py-8 bg-neutral-50 rounded-lg border border-neutral-border">
                  <div className="text-body font-body text-subtext-color mb-4 text-center">
                    No matching invoice found in purchase register
                  </div>
                  <Button
                    variant="brand-primary"
                    onClick={handleMatchAnother}
                    className="w-full max-w-xs"
                  >
                    Manually Select Invoice
                  </Button>
                </div>
              ) : !hasLinkedBooks ? (
                // No books entry currently linked (e.g. unmatched / pending record).
                // Avoid showing GSTR-2B fallbacks with ₹0 amounts, which looks like a
                // real-but-empty books entry.
                <div className="flex flex-col items-center justify-center py-8 bg-neutral-50 rounded-lg border border-neutral-border">
                  <div className="text-body font-body text-subtext-color text-center">
                    No books entry currently linked.
                  </div>
                  <div className="text-caption font-caption text-subtext-color mt-1 text-center">
                    Run reconciliation or match this invoice to a purchase register entry.
                  </div>
                </div>
              ) : (
                // Show normal data for other statuses
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-caption font-caption text-subtext-color">
                      Invoice Number
                    </span>
                    <span className={`text-body-bold font-body-bold ${invoiceNumberMismatch ? 'text-error-600' : 'text-default-font'}`}>
                      {record.booksInvoiceNumber || record.invoiceNumber || '-'}
                    </span>
                  </div>

                  <div className="flex flex-col gap-1">
                    <span className="text-caption font-caption text-subtext-color">
                      Date
                    </span>
                    <span className={`text-body-bold font-body-bold ${invoiceDateMismatch ? 'text-error-600' : 'text-default-font'}`}>
                      {record.booksInvoiceDate || record.invoiceDate || '-'}
                    </span>
                  </div>

                  <div className="flex flex-col gap-1">
                    <span className="text-caption font-caption text-subtext-color">
                      GST No
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {record.supplierGSTIN}
                    </span>
                  </div>

                  <div className="border-t border-neutral-border pt-3 mt-2">
                    <div className="flex justify-between mb-2">
                      <span className="text-body font-body text-subtext-color">
                        Taxable Value
                      </span>
                      <span className={`text-body-bold font-body-bold ${taxableValueMismatch ? 'text-error-600' : 'text-default-font'}`}>
                        {formatCurrency(record.booksTaxableValue)}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-body font-body text-subtext-color">
                        IGST
                      </span>
                      <span className={`text-body-bold font-body-bold ${igstMismatch ? 'text-error-600' : 'text-default-font'}`}>
                        {formatCurrency(record.booksIGST)}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-body font-body text-subtext-color">
                        CGST
                      </span>
                      <span className={`text-body-bold font-body-bold ${cgstMismatch ? 'text-error-600' : 'text-default-font'}`}>
                        {formatCurrency(record.booksCGST)}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-body font-body text-subtext-color">
                        SGST
                      </span>
                      <span className={`text-body-bold font-body-bold ${sgstMismatch ? 'text-error-600' : 'text-default-font'}`}>
                        {formatCurrency(record.booksSGST)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-neutral-border">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Total Amount
                      </span>
                      <span className={`text-body-bold font-body-bold ${totalAmountMismatch ? 'text-error-600' : 'text-success-600'}`}>
                        {formatCurrency(record.booksAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - GSTR-2B Entry (hidden for missing_in_2b) */}
            {!isMissingIn2B && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2 mb-2">
                  <IconWithBackground
                    variant="brand"
                    size="small"
                    icon={<FeatherFileText />}
                  />
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    GSTR-2B Entry
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-caption font-caption text-subtext-color">
                    Invoice Number
                  </span>
                  <span className={`text-body-bold font-body-bold ${invoiceNumberMismatch ? 'text-error-600' : 'text-default-font'}`}>
                    {record.invoiceNumber}
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-caption font-caption text-subtext-color">
                    Date
                  </span>
                  <span className={`text-body-bold font-body-bold ${invoiceDateMismatch ? 'text-error-600' : 'text-default-font'}`}>
                    {record.invoiceDate}
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-caption font-caption text-subtext-color">
                    GST No
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {record.supplierGSTIN}
                  </span>
                </div>

                <div className="border-t border-neutral-border pt-3 mt-2">
                  <div className="flex justify-between mb-2">
                    <span className="text-body font-body text-subtext-color">
                      Taxable Value
                    </span>
                    <span className={`text-body-bold font-body-bold ${taxableValueMismatch ? 'text-error-600' : 'text-default-font'}`}>
                      {formatCurrency(record.gstr2bTaxableValue)}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-body font-body text-subtext-color">
                      IGST
                    </span>
                    <span className={`text-body-bold font-body-bold ${igstMismatch ? 'text-error-600' : 'text-default-font'}`}>
                      {formatCurrency(record.gstr2bIGST)}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-body font-body text-subtext-color">
                      CGST
                    </span>
                    <span className={`text-body-bold font-body-bold ${cgstMismatch ? 'text-error-600' : 'text-default-font'}`}>
                      {formatCurrency(record.gstr2bCGST)}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-body font-body text-subtext-color">
                      SGST
                    </span>
                    <span className={`text-body-bold font-body-bold ${sgstMismatch ? 'text-error-600' : 'text-default-font'}`}>
                      {formatCurrency(record.gstr2bSGST)}
                    </span>
                  </div>
                  {record.gstr2bCess > 0 && (
                    <div className="flex justify-between mb-2">
                      <span className="text-body font-body text-subtext-color">
                        CESS
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {formatCurrency(record.gstr2bCess)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-neutral-border">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Total Amount
                    </span>
                    <span className={`text-body-bold font-body-bold ${totalAmountMismatch ? 'text-error-600' : 'text-success-600'}`}>
                      {formatCurrency(record.gstr2bAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Reconciliation Notes */}
          <div className="mx-6 mb-2">
            {(() => {
              const notes: string[] = [];
              const bgColor =
                record.status === 'matched' ? 'bg-success-50 border-success-200' :
                record.status === 'missing_in_books' || record.status === 'missing_in_2b' ? 'bg-error-50 border-error-200' :
                'bg-warning-50 border-warning-200';

              if (record.status === 'excluded') {
                const reasonLabel = record.exclusionReason ? exclusionReasonLabels[record.exclusionReason] : 'Unknown';
                notes.push(`This invoice has been excluded from ITC claiming.`);
                notes.push(`Reason: ${reasonLabel}.`);
                notes.push('Click "Restore to Pending" to move it back for reconciliation.');
              } else if (record.status === 'matched') {
                notes.push('GSTIN, Invoice Number, Date, and all amounts match within ₹1 tolerance.');
                notes.push('No action needed.');
              } else if (record.status === 'suggestive_invoice_mismatch') {
                notes.push(`Invoice number differs — Books: "${record.booksInvoiceNumber || '-'}", 2B: "${record.invoiceNumber}".`);
                notes.push('All amounts match within ₹1 tolerance.');
                notes.push('Verify the correct invoice number and update in your books if needed.');
              } else if (record.status === 'suggestive_date_mismatch') {
                notes.push(`Date differs — Books: "${record.booksInvoiceDate || '-'}", 2B: "${record.invoiceDate}".`);
                notes.push('Invoice number and all amounts match.');
                notes.push('Check the correct invoice date with the supplier.');
              } else if (record.status === 'suggestive_amount_mismatch') {
                const diffs: string[] = [];
                if (Math.abs(record.booksTaxableValue - record.gstr2bTaxableValue) > 1) {
                  diffs.push(`Taxable Value: Books ${formatCurrency(record.booksTaxableValue)} vs 2B ${formatCurrency(record.gstr2bTaxableValue)}`);
                }
                if (Math.abs(record.booksCGST - record.gstr2bCGST) > 1) {
                  diffs.push(`CGST: Books ${formatCurrency(record.booksCGST)} vs 2B ${formatCurrency(record.gstr2bCGST)}`);
                }
                if (Math.abs(record.booksSGST - record.gstr2bSGST) > 1) {
                  diffs.push(`SGST: Books ${formatCurrency(record.booksSGST)} vs 2B ${formatCurrency(record.gstr2bSGST)}`);
                }
                if (Math.abs(record.booksIGST - record.gstr2bIGST) > 1) {
                  diffs.push(`IGST: Books ${formatCurrency(record.booksIGST)} vs 2B ${formatCurrency(record.gstr2bIGST)}`);
                }
                if (Math.abs(record.booksAmount - record.gstr2bAmount) > 1) {
                  diffs.push(`Total: Books ${formatCurrency(record.booksAmount)} vs 2B ${formatCurrency(record.gstr2bAmount)}`);
                }
                notes.push('GSTIN, Invoice Number, and Date match.');
                notes.push(`Amount differences found: ${diffs.join('; ')}.`);
                notes.push('Verify amounts with the supplier or check for credit/debit notes.');
              } else if (record.status === 'missing_in_books') {
                notes.push('This invoice from GSTR-2B has no matching entry in your purchase register.');
                notes.push('The supplier has reported this invoice in their GSTR-1.');
                notes.push('Record this bill in Zoho or manually match it to an existing entry.');
              } else if (record.status === 'missing_in_2b') {
                notes.push('This purchase register entry was not found in GSTR-2B.');
                notes.push('The supplier may not have filed this invoice in their GSTR-1 yet.');
                notes.push('Follow up with the supplier to ensure it is reported in the next filing.');
              }

              return (
                <div className={`rounded-lg border px-4 py-3 ${bgColor}`}>
                  <ol className="list-decimal list-inside flex flex-col gap-1">
                    {notes.map((note, i) => (
                      <li key={i} className="text-body font-body text-default-font">{note}</li>
                    ))}
                  </ol>
                </div>
              );
            })()}
          </div>

          {/* Activity Log */}
          {logs.length > 0 && (
            <div className="mx-6 mb-2">
              <div className="text-caption-bold font-caption-bold text-subtext-color mb-2">Activity Log</div>
              <div className="flex flex-col gap-1 max-h-[140px] overflow-auto rounded border border-neutral-border bg-neutral-50 px-3 py-2">
                {logsLoading ? (
                  <span className="text-caption font-caption text-subtext-color">Loading...</span>
                ) : (
                  logs.map((log) => {
                    const actionLabels: Record<string, string> = {
                      auto_matched: 'Auto-matched',
                      manual_matched: 'Manually matched',
                      verified: 'Verified',
                      unmatched: 'Unmatched',
                      excluded: 'Excluded',
                      restored: 'Restored',
                    };
                    const date = new Date(log.created_at);
                    const timeStr = isNaN(date.getTime()) ? log.created_at : date.toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    });
                    return (
                      <div key={log.id} className="flex items-start gap-2 text-caption font-caption">
                        <span className="text-subtext-color whitespace-nowrap">{timeStr}</span>
                        <span className="text-default-font font-semibold">{actionLabels[log.action] || log.action}</span>
                        {log.notes && <span className="text-subtext-color truncate" title={log.notes}>— {log.notes}</span>}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Exclude Dropdown */}
          {showExcludeDropdown && (
            <div className="mx-6 mb-2 flex items-center gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3">
              <select
                value={excludeReason}
                onChange={(e) => setExcludeReason(e.target.value as ExclusionReason | '')}
                className="flex-1 px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
              >
                <option value="">Select reason...</option>
                <option value="blocked">Blocked ITC</option>
                <option value="personal_expense">Not to claim / Personal expense</option>
                <option value="credit_lapsed" disabled={!isCreditLapseEnabled}>
                  Credit lapsed (due date crossed){!isCreditLapseEnabled ? ' — not yet applicable' : ''}
                </option>
              </select>
              <Button
                variant="destructive-primary"
                size="small"
                onClick={handleExclude}
                disabled={!excludeReason || isExcluding}
                loading={isExcluding}
              >
                Confirm Exclude
              </Button>
              <Button
                variant="neutral-tertiary"
                size="small"
                onClick={() => { setShowExcludeDropdown(false); setExcludeReason(''); }}
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Verify Reason Dropdown */}
          {showVerifyDropdown && (
            <div className="mx-6 mb-2 flex flex-col gap-3 rounded-lg border border-success-200 bg-success-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <select
                  value={verifyReason}
                  onChange={(e) => {
                    const val = e.target.value as 'manual_match' | 'others' | '';
                    setVerifyReason(val);
                    if (val !== 'others') setVerifyComment('');
                  }}
                  className="flex-1 px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                >
                  <option value="">Select reason...</option>
                  <option value="manual_match">Manual Match</option>
                  <option value="others">Others</option>
                </select>
                <Button
                  variant="brand-primary"
                  size="small"
                  onClick={handleMarkAsVerified}
                  disabled={!verifyReason || (verifyReason === 'others' && !verifyComment.trim()) || isVerifying}
                  loading={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Confirm Verify'}
                </Button>
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={() => { setShowVerifyDropdown(false); setVerifyReason(''); setVerifyComment(''); }}
                >
                  Cancel
                </Button>
              </div>
              {verifyReason === 'others' && (
                <input
                  type="text"
                  value={verifyComment}
                  onChange={(e) => setVerifyComment(e.target.value)}
                  placeholder="Enter a comment for this match..."
                  className="w-full px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                />
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex flex-col gap-3 border-t border-neutral-border px-6 py-4">
            {verificationError && (
              <div className="text-body font-body text-error-600 px-3 py-2 bg-error-50 rounded">
                {verificationError}
              </div>
            )}

            {/* Show exclusion reason badge for excluded entries */}
            {isExcluded && record.exclusionReason && (
              <div className="flex items-center gap-2 px-3 py-2 bg-neutral-50 rounded">
                <span className="text-body font-body text-subtext-color">Excluded reason:</span>
                <Badge variant="warning">{exclusionReasonLabels[record.exclusionReason] || record.exclusionReason}</Badge>
              </div>
            )}

            <div className="flex items-center justify-between">
              {isExcluded ? (
                <div className="flex gap-3 justify-end w-full">
                  <Button variant="neutral-secondary" onClick={onClose}>Close</Button>
                  <Button
                    variant="brand-primary"
                    onClick={handleRestore}
                    disabled={isRestoring}
                    loading={isRestoring}
                  >
                    {isRestoring ? 'Restoring...' : 'Restore to Pending'}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-3 justify-between w-full">
                  <div className="flex gap-3">
                    <Button
                      variant="destructive-secondary"
                      size="medium"
                      onClick={() => setShowExcludeDropdown(!showExcludeDropdown)}
                      disabled={showExcludeDropdown}
                    >
                      Exclude
                    </Button>
                    {record.status === 'matched' && (
                      <Button
                        variant="neutral-secondary"
                        size="medium"
                        onClick={handleUnmatch}
                        disabled={isUnmatching}
                        loading={isUnmatching}
                      >
                        {isUnmatching ? 'Unmatching...' : 'Unmatch'}
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    {canBeVerified && !isMissingInBooks && (
                      <Button variant="neutral-secondary" onClick={handleMatchAnother}>
                        Match Another
                      </Button>
                    )}
                    {canBeVerified ? (
                      <Button
                        variant="brand-primary"
                        onClick={() => setShowVerifyDropdown(!showVerifyDropdown)}
                        disabled={showVerifyDropdown || isVerifying}
                      >
                        Mark as Verified
                      </Button>
                    ) : (
                      <Button variant="neutral-secondary" onClick={onClose}>Close</Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Nested Modals */}
      <InvoicePickerModal
        isOpen={showInvoicePicker}
        onClose={() => setShowInvoicePicker(false)}
        gstr2bInvoiceId={record.id}
        supplierName={record.supplierName}
        onInvoiceSelected={handleInvoiceSelected}
      />

      <MatchConfirmationModal
        isOpen={showMatchConfirmation}
        onClose={() => {
          setShowMatchConfirmation(false);
          setSelectedInvoice(null);
        }}
        gstr2bRecord={record}
        selectedInvoice={selectedInvoice!}
        onConfirm={handleConfirmMatch}
      />
    </>
  );
};

export default InvoiceDetailsModal;

